import { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import __gunRoom_asset from '@/assets/gun-room/ranger-rifles.png.asset.json';
const gunRoom = __gunRoom_asset.url;

const FeaturedExhibitSection = forwardRef<HTMLElement>((_props, ref) => {
  return (
    <section ref={ref} className="py-24 lg:py-32 bg-hero text-hero-text relative overflow-hidden">
      {/* Subtle background texture */}
      <div className="absolute inset-0 opacity-[0.02] bg-[radial-gradient(circle_at_50%_50%,_hsl(var(--hero-text))_1px,_transparent_1px)] bg-[length:24px_24px]" />
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Content Side */}
          <div className="order-2 lg:order-1">
            <p className="font-sans text-xs tracking-ultrawide uppercase text-brass mb-6">
              Featured Exhibit
            </p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-normal text-hero-text leading-tight tracking-wide">
              Frontier Justice
            </h2>
            <p className="mt-2 font-serif text-xl text-hero-text/80 italic">
              Texas Rangers in the Hill Country
            </p>
            
            <div className="w-16 h-px bg-gradient-to-r from-brass/50 to-transparent my-8" />
            
            <p className="font-serif text-base text-hero-text/70 leading-relaxed mb-6">
              Explore the legacy of Texas Rangers who shaped law and order in the rugged Hill Country, 
              with authentic artifacts from the Schreiner family collection. From the Republic era through 
              the twentieth century, discover the men who carried the badge.
            </p>
            <p className="font-serif text-base text-hero-text/70 leading-relaxed mb-10">
              This permanent exhibit features original badges, firearms, photographs, and personal effects 
              of Rangers with direct connections to the Y.O. Ranch and Kerr County.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/texas-ranger-hall"
                className="inline-flex items-center gap-3 font-sans text-sm tracking-widest uppercase text-hero-text border border-hero-text/30 px-6 py-4 hover:bg-hero-text/5 hover:border-hero-text/50 transition-all duration-300 group"
              >
                View Exhibit
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </div>
          </div>
          
          {/* Image Side */}
          <div className="order-1 lg:order-2 relative group">
            <div className="aspect-[4/3] overflow-hidden">
              <img 
                src={gunRoom}
                alt="Texas Ranger rifle case in the Y.O. Ranch Headquarters gun room, featuring lever-action rifles and Captain Clint Peoples memorabilia"
                loading="lazy"
                width={616}
                height={449}
                decoding="async"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </div>
            {/* Decorative corners with hover animation */}
            <div className="absolute -bottom-4 -left-4 w-24 h-24 border-b border-l border-brass/30 transition-all duration-500 group-hover:w-28 group-hover:h-28 group-hover:border-brass/50" />
            <div className="absolute -top-4 -right-4 w-24 h-24 border-t border-r border-brass/30 transition-all duration-500 group-hover:w-28 group-hover:h-28 group-hover:border-brass/50" />
          </div>
        </div>
      </div>
    </section>
  );
});
FeaturedExhibitSection.displayName = 'FeaturedExhibitSection';

export default FeaturedExhibitSection;